<template>
  <div class="scpm-insight-chart-container" ref="chartPopupWrapperRef">
    <ChartPopupTrigger
      :class="{
        active: needCheckPerformance && isTitleClickable
      }"
      v-bind="{ keyrender: chartKey }"
      @toggle="handleToggleChart"
    >
      <slot></slot>
    </ChartPopupTrigger>

    <teleport to="body">
      <InsightChartContainer
        v-if="isShowChart"
        :wrapper="chartPopupWrapperRef"
        :selectedMetric="selectedMetric"
        :selectedProductType="selectedProductType"
        :selectedTargets="selectedTargets"
        :selectedSubProduct="selectedSubProduct"
        :isShowChart="isShowChart"
        :metricData="metricData"
        :individualItems="individualItems"
        :startDate="startDate"
        :endDate="endDate"
        :paramDownloadChart="paramDownloadChart"
        :collInterval="collInterval"
        :options="getInsightChartOptions"
        :parentClass="parentClass"
        :isNow="isNow"
        :showLastValue="showLastValue"
        :customPopupStyle="customPopupStyle"
        @update:isShowChart="isShowChart = $event"
        @chartChange="handleChartChange"
        :chartKey="chartKey"
        :refreshKey="refreshKey"
      />
    </teleport>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, onMounted, onUnmounted, ref, toRefs, watch } from 'vue';
import { isEmpty } from 'lodash';
import useInsightChart from '@/composables/useInsightChart';
import ChartPopupTrigger from '@/containers/components/chart/PopUp/ChartPopupTrigger.vue';
import InsightChartContainer from '@/containers/components/chart/PopUp/InsightChartContainer.vue';
import store from '@/store';
import { scpmUtil } from '@/utils/scpmUtil';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import { EVENT_POPUP } from '@/components/constants/constants';

const props = defineProps({
  selectedMetric: {
    type: Object,
    required: true
  },
  selectedTargets: {
    type: [Array, Object],
    required: true
  },
  selectedSubProduct: {
    type: String,
    required: false
  },
  start: {
    type: String,
    required: false
  },
  end: {
    type: String,
    required: false
  },
  selectedProductType: {
    type: String,
    required: true
  },
  collInterval: {
    type: String,
    default: '1m'
  },
  insightChartOptions: {
    type: Object,
    default: () => ({})
  },
  isNow: {
    type: Boolean,
    default: false
  },
  showLastValue: {
    type: Boolean,
    default: () => true
  },
  parentClass: {
    type: String,
    default: ''
  },
  needCheckPerformance: {
    type: Boolean,
    default: false
  },
  refreshKey: {
    type: String,
    default: ''
  },
  customPopupStyle: {
    type: Object,
    default: () => ({
      alignCenter: true,
      resizeHeight: 0,
      marginTop: 11
    })
  }
});

const emit = defineEmits(['toggle']);
const chartKey = scpmUtil.generateRandomKey();

const chartPopupWrapperRef = ref(null);
const chartConfig = toRefs(props);
const {
  isShowChart,
  metricData,
  individualItems,
  startDate,
  endDate,
  paramDownloadChart,
  toggleChart,
  hideChart,
  prepareChartData,
  handleChartChange,
  isTitleClickable
} = useInsightChart({
  selectedMetric: chartConfig.selectedMetric,
  selectedTargets: chartConfig.selectedTargets,
  selectedSubProduct: chartConfig.selectedSubProduct,
  start: chartConfig.start,
  end: chartConfig.end,
  selectedProductType: chartConfig.selectedProductType,
  collInterval: chartConfig.collInterval
});

const getInsightChartOptions = computed(() => {
  const option = chartConfig.insightChartOptions.value;
  const baseOption = !isEmpty(option) ? option : chartConfig.selectedMetric.value;
  const productTypeOption = { productType: chartConfig.selectedProductType.value };
  const chartUnitOption = {
    displayUnit: baseOption.displayUnit || chartConfig.selectedMetric.value.metricUnit
  };

  return {
    ...baseOption,
    ...productTypeOption,
    ...chartUnitOption
  };
});

const getZIndex = (element) => {
  const style = window.getComputedStyle(element);
  return parseInt(style.zIndex, 10) || -1;
};

const isOutsideChartContainer = (chartContainer, target) => {
  return !chartContainer.contains(target);
};

const isSelfClick = (event) => {
  const targetWithKey = document.querySelector(`.trigger-element-wrapper[keyrender='${chartKey}']`);

  return targetWithKey && targetWithKey.contains(event.target);
};

const handleClickOutside = (event) => {
  const chartContainer = document.querySelector('.chart-container-element-' + chartKey);
  const bodyElement = document.querySelector('body');

  if (!chartContainer || !bodyElement) return;

  const targetZIndex = getZIndex(event.target);
  const chartContainerZIndex = getZIndex(chartContainer);

  let currentTarget = event.target;

  while (currentTarget && currentTarget !== bodyElement && bodyElement.contains(currentTarget)) {
    const currentTargetZIndex = getZIndex(currentTarget);

    if (currentTargetZIndex > chartContainerZIndex) {
      return;
    }

    currentTarget = currentTarget.parentElement;
  }

  if (
    targetZIndex <= chartContainerZIndex &&
    isOutsideChartContainer(chartContainer, event.target) &&
    !isSelfClick(event)
  ) {
    hideChart();
  }
};

const hideChartOnScroll = () => {
  const containers = document.body.querySelectorAll('.simplebar-content-wrapper');

  if (containers && containers.length) {
    containers.forEach((el) => el.addEventListener('scroll', hideChart));
  }

  const tableContainer = document.querySelector('.lego-paging-table');
  const setScrollElement = tableContainer && tableContainer.querySelector('.set-scroll');

  if (setScrollElement) {
    setScrollElement.addEventListener('scroll', hideChart);
  }
};

const handleToggleChart = () => {
  if (props.needCheckPerformance && !isTitleClickable.value) return;

  toggleChart();
};

watch(
  () => isShowChart.value,
  (value) => {
    if (value) {
      store.dispatch('chartPopup/setPopupVisibleStates', chartKey);
    } else {
      store.dispatch('chartPopup/removePopup', chartKey);
    }

    emit('toggle', value);
    legoEventBus.$emit('click-outside');
    legoEventBus.$emit('click-outside-datePicker');
    legoEventBus.$emit(EVENT_POPUP.INSIGHT_CHART, value);
  }
);

watch(
  () => props.refreshKey,
  (self, prev) => {
    if (isShowChart.value && self !== prev) {
      prepareChartData();
    }
  }
);
onMounted(async () => {
  if (props.needCheckPerformance) {
    await prepareChartData(true);
  }

  document.addEventListener('mousedown', handleClickOutside);
  hideChartOnScroll();
});

onBeforeMount(() => {
  emit('toggle', false);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>

<style scoped lang="scss">
.scpm-insight-chart-container {
  width: 100%;
}
</style>
